import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { InputBase, Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { SendIcon } from "../../shared/icons";
import { usePrevious } from "../../shared/usePrevious";

export type ComposerSeed = { key: number; text: string };

type AssistantComposerProps = {
  disabled: boolean;
  onSend: (text: string) => void;
  placeholder?: string;
  seed?: ComposerSeed | null;
  isQuiet?: boolean;
};

export const AssistantComposer = ({ disabled, onSend, placeholder, seed, isQuiet = false }: AssistantComposerProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wasDisabled = usePrevious(disabled);

  useEffect(() => {
    if (!seed) return;
    setText(seed.text);
    inputRef.current?.focus();
  }, [seed]);

  useEffect(() => {
    const hasLostFocus = document.activeElement === null || document.activeElement === document.body;
    if (wasDisabled && !disabled && hasLostFocus) inputRef.current?.focus();
  }, [wasDisabled, disabled]);

  const canSend = !disabled && text.trim().length > 0;
  const isFilled = canSend && !isQuiet;

  const submit = () => {
    if (!canSend) return;
    onSend(text.trim());
    setText("");
  };

  return (
    <Stack
      direction="row"
      onClick={() => inputRef.current?.focus()}
      sx={{
        "gap": 1,
        "alignItems": "flex-end",
        "pl": 2,
        "pr": 1,
        "py": 0.75,
        "borderRadius": 1,
        "border": "1px solid",
        "borderColor": theme => theme.palette.grey[400],
        "bgcolor": "background.paper",
        "cursor": "text",
        "transition": "border-color 120ms",
        "&:hover, &:focus-within": { borderColor: theme => theme.palette.purple[500] },
      }}
    >
      <InputBase
        id="assistant-composer"
        inputRef={inputRef}
        fullWidth
        multiline
        minRows={1}
        maxRows={8}
        placeholder={placeholder ?? t("Assistant.Composer.Placeholder")}
        inputProps={{ "aria-label": t("Assistant.Composer.Placeholder") }}
        value={text}
        onChange={event => setText(event.target.value)}
        onKeyDown={event => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
        sx={{
          "py": 0.75,
          "fontSize": theme => theme.typography.pxToRem(14),
          "lineHeight": 1.5,
          "& textarea": { p: 0, lineHeight: 1.5 },
          "& textarea::placeholder": { color: theme => theme.palette.text.disabled, opacity: 1 },
        }}
      />
      <Button
        size="small"
        onClick={submit}
        disabled={!canSend}
        endIcon={<SendIcon size={14} />}
        sx={{
          flexShrink: 0,
          minHeight: 28,
          height: 28,
          mb: "1px",
          px: 1.5,
          ...(!isFilled && {
            "bgcolor": theme => theme.palette.grey[300],
            "borderColor": theme => theme.palette.grey[300],
            "color": theme => theme.palette.text.disabled,
            "boxShadow": "none",
            "&:hover": {
              bgcolor: theme => theme.palette.grey[300],
              borderColor: theme => theme.palette.grey[300],
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              bgcolor: theme => theme.palette.grey[300],
              borderColor: theme => theme.palette.grey[300],
              color: theme => theme.palette.text.disabled,
            },
          }),
        }}
      >
        {t("Assistant.Composer.Send")}
      </Button>
    </Stack>
  );
};
